import React, { useState } from 'react';
import VoxSmythIcons, { VoxSmythAnimations } from './VoxSmythIcons';
import './VoxSmythIcons.css';

// Enhanced User Menu - "Literary Command Center" themed
const EnhancedUserMenu = ({ 
  user,
  onNavigateHome,
  onNavigateLibrary,
  onNavigateDashboard,
  onNavigatePricing,
  onNavigateVoices,
  onNavigateVoiceActorPortal,
  onLogout
}) => {
  const [hoveredItem, setHoveredItem] = useState(null);

  // Enhanced Design Tokens for User Menu
  const MENU_TOKENS = {
    colors: {
      // Core Palette
      slate: '#2C3A4C',
      antique_gold: '#D7B377',
      warm_parchment: '#F9F6F0',
      
      // Menu-specific
      command_dark: '#1A202C',
      literary_gold: '#DAA520',
      menu_card: '#FFFFFF',
      hover_glow: '#F7FAFC',
      divider: '#E8DCC0'
    },
    typography: {
      main_title: 'DM Serif Display, Georgia, serif',
      menu_item: 'DM Sans, sans-serif',
      description: 'Inter, sans-serif',
      user_info: 'DM Sans, sans-serif'
    }
  };

  const menuItems = [
    {
      id: 'library',
      title: 'Your Library',
      description: 'Browse and manage your audiobook collection',
      icon: VoxSmythIcons.Library,
      color: MENU_TOKENS.colors.literary_gold,
      onClick: onNavigateLibrary
    },
    {
      id: 'voices',
      title: 'Voice Library',
      description: 'Explore and customize narrator voices',
      icon: VoxSmythIcons.VintageMicrophone,
      color: MENU_TOKENS.colors.antique_gold,
      onClick: onNavigateVoices
    },
    {
      id: 'vocal-atelier',
      title: 'Voice Foundry',
      description: 'Professional voice actor collaboration studio',
      icon: VoxSmythIcons.VoiceStudio,
      color: '#9B59B6', // Purple color for artisan/atelier theme
      onClick: onNavigateVoiceActorPortal
    },
    {
      id: 'accounts',
      title: 'Account Settings',
      description: 'Manage your account preferences and settings',
      icon: VoxSmythIcons.Settings,
      color: '#3498DB',
      onClick: onNavigateDashboard
    },
    {
      id: 'pricing',
      title: 'Subscription Plans',
      description: 'Upgrade or manage your membership',
      icon: VoxSmythIcons.Earnings,
      color: '#E74C3C',
      onClick: onNavigatePricing
    }
  ];

  const getTierDisplayInfo = (tier) => {
    const tierInfo = {
      'free': { name: 'Free Explorer', color: '#95A5A6', icon: '🌟' },
      'listener': { name: 'The Listener', color: '#3498DB', icon: '📚' },
      'director': { name: 'The Director', color: '#E74C3C', icon: '🎭' },
      'creator': { name: 'The Creator', color: '#9B59B6', icon: '✒️' }
    };
    return tierInfo[tier] || tierInfo['free'];
  };

  const userTierInfo = getTierDisplayInfo(user?.tier);

  return (
    <div 
      className="enhanced-user-menu"
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${MENU_TOKENS.colors.command_dark} 0%, ${MENU_TOKENS.colors.slate} 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Global Styles */}
      <style>{`
        ${VoxSmythAnimations}
      `}</style>

      {/* Background Pattern */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 20% 30%, ${MENU_TOKENS.colors.literary_gold}10 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${MENU_TOKENS.colors.antique_gold}08 0%, transparent 50%)
          `,
          pointerEvents: 'none'
        }}
      />

      {/* Header */}
      <header style={{
        position: 'relative',
        zIndex: 1,
        padding: '32px 24px',
        borderBottom: `1px solid ${MENU_TOKENS.colors.divider}30`
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${userTierInfo.color} 0%, ${MENU_TOKENS.colors.antique_gold} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}>
              {userTierInfo.icon}
            </div>
            <div>
              <h1
                style={{
                  fontFamily: MENU_TOKENS.typography.main_title,
                  fontSize: '24px',
                  color: 'white',
                  margin: '0 0 4px 0'
                }}
              >
                Welcome back, {user?.first_name || user?.email?.split('@')[0] || 'Reader'}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    fontFamily: MENU_TOKENS.typography.user_info,
                    fontSize: '14px',
                    color: userTierInfo.color,
                    fontWeight: '600'
                  }}
                >
                  {userTierInfo.name}
                </span>
                <div style={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.4)'
                }} />
                <span
                  style={{
                    fontFamily: MENU_TOKENS.typography.description,
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.7)'
                  }}
                >
                  {user?.email}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={onNavigateHome}
            style={{
              background: 'transparent',
              border: `1px solid ${MENU_TOKENS.colors.antique_gold}60`,
              borderRadius: '8px',
              color: 'white',
              padding: '12px 20px',
              fontSize: '14px',
              fontFamily: MENU_TOKENS.typography.menu_item,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = MENU_TOKENS.colors.antique_gold;
              e.target.style.background = `${MENU_TOKENS.colors.antique_gold}15`;
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = `${MENU_TOKENS.colors.antique_gold}60`;
              e.target.style.background = 'transparent';
            }}
          >
            <VoxSmythIcons.Home size={14} color={MENU_TOKENS.colors.antique_gold} />
            Home
          </button>
        </div>
      </header>

      {/* Main Menu */}
      <main style={{
        padding: '40px 24px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}>
            {menuItems.map((item) => (
              <div
                key={item.id}
                style={{
                  background: MENU_TOKENS.colors.menu_card,
                  borderRadius: '16px',
                  padding: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${MENU_TOKENS.colors.divider}40`,
                  transform: hoveredItem === item.id ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: hoveredItem === item.id 
                    ? `0 12px 32px ${item.color}20`
                    : '0 4px 12px rgba(26, 32, 44, 0.08)'
                }}
                onClick={item.onClick}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: `${item.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <item.icon size={24} color={item.color} />
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        fontFamily: MENU_TOKENS.typography.menu_item,
                        fontSize: '18px',
                        fontWeight: '600',
                        color: MENU_TOKENS.colors.slate,
                        margin: '0 0 8px 0'
                      }}
                    >
                      {item.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: MENU_TOKENS.typography.description,
                        fontSize: '14px',
                        color: `${MENU_TOKENS.colors.slate}80`,
                        margin: 0,
                        lineHeight: '1.4'
                      }}
                    >
                      {item.description}
                    </p>
                  </div>

                  <VoxSmythIcons.ArrowRight 
                    size={20} 
                    color={hoveredItem === item.id ? item.color : `${MENU_TOKENS.colors.slate}40`} 
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div style={{
            background: MENU_TOKENS.colors.menu_card,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${MENU_TOKENS.colors.divider}40`,
            marginBottom: '24px'
          }}>
            <h3
              style={{
                fontFamily: MENU_TOKENS.typography.menu_item,
                fontSize: '18px',
                fontWeight: '600',
                color: MENU_TOKENS.colors.slate,
                margin: '0 0 16px 0'
              }}
            >
              Quick Actions
            </h3>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={onNavigateLibrary}
                style={{
                  background: `${MENU_TOKENS.colors.literary_gold}15`,
                  border: `1px solid ${MENU_TOKENS.colors.literary_gold}30`,
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontFamily: MENU_TOKENS.typography.menu_item,
                  color: MENU_TOKENS.colors.literary_gold,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `${MENU_TOKENS.colors.literary_gold}25`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `${MENU_TOKENS.colors.literary_gold}15`;
                }}
              >
                <VoxSmythIcons.Play size={14} color={MENU_TOKENS.colors.literary_gold} />
                Continue Reading
              </button>
              
              <button
                onClick={onNavigateVoices}
                style={{
                  background: `${MENU_TOKENS.colors.antique_gold}15`,
                  border: `1px solid ${MENU_TOKENS.colors.antique_gold}30`,
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontFamily: MENU_TOKENS.typography.menu_item,
                  color: MENU_TOKENS.colors.antique_gold,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `${MENU_TOKENS.colors.antique_gold}25`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `${MENU_TOKENS.colors.antique_gold}15`;
                }}
              >
                <VoxSmythIcons.VintageMicrophone size={14} color={MENU_TOKENS.colors.antique_gold} />
                Browse Voices
              </button>
            </div>
          </div>

          {/* Logout Section */}
          <div style={{
            background: 'rgba(231, 76, 60, 0.05)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(231, 76, 60, 0.2)',
            textAlign: 'center'
          }}>
            <button
              onClick={onLogout}
              style={{
                background: 'transparent',
                border: '1px solid #E74C3C40',
                borderRadius: '8px',
                color: '#E74C3C',
                padding: '12px 24px',
                fontSize: '14px',
                fontFamily: MENU_TOKENS.typography.menu_item,
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                margin: '0 auto',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#E74C3C';
                e.target.style.background = 'rgba(231, 76, 60, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#E74C3C40';
                e.target.style.background = 'transparent';
              }}
            >
              <VoxSmythIcons.Logout size={14} color="#E74C3C" />
              Sign Out
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EnhancedUserMenu;